import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrEdges } from '@/api/ivrEdges';
import { ivrNodes } from '@/api/ivrNodes';
import type { IvrNodeRow } from '@/api/ivrNodes';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import { formatRef } from '@/lib/formatRef';
import { Trash2, X } from 'lucide-react';

interface Props {
  edgeId: string;
  fromNodeId: string;
  toNodeId: string | null;
  branchKey: string;
  treeId: string;
  treeNodes: IvrNodeRow[];
  onClose: () => void;
  canMutate: boolean;
  /** Position in the viewport (px from top-left of screen) */
  x?: number;
  y?: number;
}

export function EdgeInlineEditor({
  edgeId,
  fromNodeId,
  toNodeId,
  branchKey,
  treeId,
  treeNodes,
  onClose,
  canMutate,
  x = 400,
  y = 300,
}: Props) {
  const qc = useQueryClient();
  const panelRef = useRef<HTMLDivElement>(null);
  const [newToNodeId, setNewToNodeId] = useState(toNodeId ?? '');
  const [error, setError] = useState('');

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Delete edge
  const deleteEdge = useMutation({
    mutationFn: () => ivrEdges.remove(edgeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdges', fromNodeId] });
      onClose();
    },
  });

  // Update: delete old + create new with same branch_key but different to_node
  const updateEdge = useMutation({
    mutationFn: async () => {
      if (!newToNodeId) throw new Error('Select a target node');
      await ivrEdges.remove(edgeId);
      await ivrEdges.create(fromNodeId, {
        branch_key: branchKey,
        to_node_id: newToNodeId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdges', fromNodeId] });
      onClose();
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Failed to update edge');
    },
  });

  // Keep panel within viewport
  const panelW = 280;
  const panelH = 220;
  const clampedX = Math.min(Math.max(x, 8), window.innerWidth - panelW - 8);
  const clampedY = Math.min(Math.max(y, 8), window.innerHeight - panelH - 8);

  return (
    <>
      {/* Click-away */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      <div
        ref={panelRef}
        className="fixed z-50 w-[280px] rounded-lg border border-slate-200 bg-white shadow-xl"
        style={{ left: clampedX, top: clampedY }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-slate-600">
              {branchKey}
            </span>
            <span className="text-xs text-slate-500">edge</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="px-3 py-3 space-y-3">
          {/* Target node picker */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Routes to
            </label>
            {canMutate ? (
              <Select value={newToNodeId} onValueChange={setNewToNodeId}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select target node…" />
                </SelectTrigger>
                <SelectContent>
                  {treeNodes
                    .filter((n) => n.id !== fromNodeId)
                    .map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        <span className="flex items-center gap-1.5">
                          <span className="font-mono text-[10px] text-slate-400">{formatRef(n.ref_number)}</span>
                          <span className="truncate max-w-[120px]">{n.name}</span>
                          <NodeTypeBadge type={n.node_type} className="shrink-0" />
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="text-xs text-slate-600">
                {treeNodes.find((n) => n.id === toNodeId)?.name ?? '(none)'}
              </div>
            )}
          </div>

          {error && (
            <p className="text-[11px] text-red-500">{error}</p>
          )}

          {canMutate && (
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => deleteEdge.mutate()}
                disabled={deleteEdge.isPending || updateEdge.isPending}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Delete edge
              </Button>
              <Button
                size="sm"
                className="flex-1 h-7 text-xs"
                onClick={() => updateEdge.mutate()}
                disabled={
                  !newToNodeId ||
                  newToNodeId === toNodeId ||
                  updateEdge.isPending ||
                  deleteEdge.isPending
                }
              >
                {updateEdge.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
